import { useForumType } from '@/components/hooks/useForumType';
import React from 'react';
import Button from '@/lib/vendor/@material-ui/core/src/Button';
import classNames from 'classnames';
import { useCurrentUser } from "../common/withUser";
import { useTracking } from "../../lib/analyticsEvents";
import { requestFeedbackKarmaLevelSetting } from '@/lib/instanceSettings.ts';
import { getSiteUrl } from "../../lib/vulcan-lib/utils";
import type { EditablePost, PostSubmitMeta } from '@/lib/collections/posts/helpers.ts';
import type { TypedFormApi } from '@/components/tanstack-form-components/BaseAppForm.tsx';
import { defineStyles, useStyles } from '../hooks/useStyles.tsx';
import LWTooltip from "../common/LWTooltip";

export const styles = defineStyles('PostSubmit', (theme: ThemeType) => ({
  formButton: {
    fontFamily: theme.typography.commentStyle.fontFamily,
    fontSize: 16,
    marginLeft: 5,
    paddingBottom: 4,
    fontWeight: 500,
    "&:hover": {
      background: theme.palette.buttons.hoverGrayHighlight,
    }
  },
  secondaryButton: {
    color: theme.palette.text.dim40,
  },
  submitButtons: {
    marginLeft: 'auto'
  },
  submitButton: {
    color: theme.palette.secondary.main
  },
  cancelButton: {
  },
  draft: {
  },
  feedback: {
  },
  feedbackRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    width: "100%",
  },
}));

export type PostSubmitProps = {
  formApi: TypedFormApi<EditablePost, PostSubmitMeta>,
  disabled: boolean;
  submitLabel?: string,
  cancelLabel?: string,
  saveDraftLabel?: string,
  feedbackLabel?: string,
  claudeButton?: React.ReactNode,
  cancelCallback?: (document: EditablePost) => void,
}

export const PostSubmit = ({
  formApi,
  disabled,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  saveDraftLabel = "Save as draft",
  feedbackLabel = "Request Feedback",
  claudeButton,
  cancelCallback,
}: PostSubmitProps) => {
  const { isLW, forumType } = useForumType();
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  const { captureEvent } = useTracking();
  if (!currentUser) throw Error("must be logged in to post");
  const document = formApi.state.values;

  const submitWithConfirmation = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (confirm('Warning!  This will publish your dialogue and make it visible to other users.')) {
      formApi.setFieldValue('draft', false);
      await formApi.handleSubmit();
    }
  };

  const submitWithoutConfirmation = async () =>  {
    formApi.setFieldValue('draft', false);
    await formApi.handleSubmit();
  };

  const requireConfirmation = isLW && !!document.debate;

  const onSubmitClick = requireConfirmation ? submitWithConfirmation : submitWithoutConfirmation;
  const requestFeedbackKarmaLevel = requestFeedbackKarmaLevelSetting.get(forumType)
  const showFeedbackButton = requestFeedbackKarmaLevel !== null && currentUser.karma >= requestFeedbackKarmaLevel;
  const feedbackTitle = `Request feedback from our editor.  If you don't see a notification pop up next to the Intercom icon in a few seconds, try opening Intercom and check the "Messages" panel to see if there's a new conversation there.`

  return (
    <React.Fragment>
      {!!cancelCallback &&
        <div className={classes.cancelButton}>
          <Button
            className={classNames("form-cancel", classes.formButton, classes.secondaryButton)}
            onClick={(e) => {
              e.preventDefault();
              cancelCallback(document)
            }}
          >
            {cancelLabel}
          </Button>
        </div>
      }
      <div className={classes.submitButtons}>
        <Button
          type="submit"
          onClick={onSubmitClick}
          disabled={disabled}
          className={classNames("primary-form-submit-button", classes.formButton, classes.submitButton)}
        >
          {submitLabel}
        </Button>
        {(showFeedbackButton || claudeButton) && (
          <div className={classes.feedbackRow}>
            {showFeedbackButton && (
              <LWTooltip title={feedbackTitle}>
                <Button type="submit"
                  className={classNames(classes.formButton, classes.secondaryButton, classes.feedback)}
                  disabled={disabled}
                  onClick={async () => {
                    captureEvent("feedbackRequestButtonClicked")
                    if (!!document.title) {
                      await formApi.handleSubmit({
                        successCallback: (createdPost: PostsEditMutationFragment) => {
                          const intercomProps = {
                            title: createdPost.title,
                            _id: createdPost._id,
                            url: getSiteUrl(forumType) + "posts/" + createdPost._id
                          };

                          // eslint-disable-next-line
                          window.Intercom(
                            'trackEvent',
                            'requested-feedback',
                            intercomProps
                          );
                        },
                        // The redirect here is both undesirable and might interfere with Intercom displaying the message prompt
                        skipRedirect: true,
                      });
                    }
                  }}
                >
                  {feedbackLabel}
                </Button>
              </LWTooltip>
            )}
            {claudeButton}
          </div>
        )}
        <Button type="submit"
          className={classNames(classes.formButton, classes.secondaryButton, classes.draft)}
          onClick={async () => {
            formApi.setFieldValue('draft', true);
            await formApi.handleSubmit();
          }}
          disabled={disabled}
        >
          {saveDraftLabel}
        </Button>
      </div>
    </React.Fragment>
  );
}
